import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { NotificationDropdown } from "./NotificationDropdown";
import { NotificationDetailModal } from "./NotificationDetailModal";
import { Notification } from "../types/api";

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [openNotification, setOpenNotification] = useState<Notification | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Initial load
    loadUnreadCount();

    // Poll every 30 seconds when dropdown is closed
    const pollInterval = setInterval(() => {
      if (!showDropdown) {
        loadUnreadCount();
      }
    }, 30000);

    return () => clearInterval(pollInterval);
  }, [showDropdown]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const loadUnreadCount = async () => {
    try {
      const response = await apiService.getNotificationUnreadCount();
      setUnreadCount(response.count);
    } catch (err: any) {
      console.error("Failed to load unread count:", err);
    }
  };

  const handleBellClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleViewAll = () => {
    setShowDropdown(false);
    navigate('/notifications');
  };

  return (
    <div className="relative">
      <button
        ref={bellRef}
        onClick={handleBellClick}
        className="relative px-3 py-2 bg-white hover:border-accent hover:text-accent text-ink rounded-full border border-sand-300 transition-all"
        title="Notifications"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div ref={dropdownRef}>
          <NotificationDropdown
            onClose={() => setShowDropdown(false)}
            onViewAll={handleViewAll}
            onNotificationCountChange={setUnreadCount}
            onOpenNotification={(n) => {
              setOpenNotification(n);
              setShowDropdown(false);
            }}
          />
        </div>
      )}

      {openNotification && (
        <NotificationDetailModal
          notification={openNotification}
          onClose={() => setOpenNotification(null)}
        />
      )}
    </div>
  );
};
